import {inject, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {EnvironmentService} from '../environment.service';
import {ConnectConnectionsService, ConnectOAuthAuthorizationRequest} from './connect-connections.service';

@Injectable({providedIn: 'root'})
export class ConnectOAuthPopupService {
  private readonly connections = inject(ConnectConnectionsService);
  private readonly environment = inject(EnvironmentService);

  authorize(request: ConnectOAuthAuthorizationRequest, signal?: AbortSignal): Promise<void> {
    return this.authorizeWith(nonce => firstValueFrom(this.connections.authorize({...request, browser_nonce: nonce})), signal);
  }

  authorizeWith(start: (browserNonce: string) => Promise<{url: string}>, signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) return Promise.reject(new Error('Authentication was cancelled'));
    const auth = new URL(this.environment.authBaseServerUrl || window.location.origin);
    const bootstrap = new URL('/api/v1/auth/browser', auth);
    bootstrap.searchParams.set('popup_origin', window.location.origin);
    // This first-party page sets an HttpOnly browser nonce before authenticated HTTP issues state.
    // Request a normal tab: feature-sized auxiliary windows can remain hidden in embedded browsers.
    // Keep the opener so the first-party nonce and callback retain their origin/source checks.
    const popup = window.open(bootstrap.toString(), '_blank');
    if (!popup) return Promise.reject(new Error('Allow popups to connect this account'));
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let started = false;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        clearInterval(poll);
        clearTimeout(deadline);
        signal?.removeEventListener('abort', cancel);
        try { popup.close(); } catch {}
        if (error) reject(error); else resolve();
      };
      const onMessage = (event: MessageEvent) => {
        if (event.origin !== auth.origin || event.source !== popup || !event.data || typeof event.data !== 'object') return;
        if (event.data.type === 'auth:ready' && !started) {
          if (typeof event.data.nonce !== 'string' || !/^[a-zA-Z0-9_-]{43}$/.test(event.data.nonce)) return;
          started = true;
          void Promise.resolve().then(() => start(event.data.nonce)).then(response => {
            if (settled) return;
            const target = new URL(response.url);
            if (target.protocol !== 'https:' || target.username || target.password) throw new Error('Invalid authentication destination');
            popup.location.href = target.toString();
            popup.focus();
          }).catch(() => finish(new Error('Unable to start authentication. Check your client permissions and try again.')));
        } else if (event.data.type === 'auth:callback' && started) {
          finish(event.data.ok === true ? undefined : new Error('Account authentication failed. Please try again.'));
        }
      };
      window.addEventListener('message', onMessage);
      const poll = setInterval(() => {
        if (popup.closed) finish(new Error('Authentication window was closed'));
      }, 300);
      // Match the existing five-minute Auth browser nonce and signed state lifetime.
      const deadline = setTimeout(() => finish(new Error('Authentication timed out. Please try again.')), 5 * 60 * 1000);
      const cancel = () => finish(new Error('Authentication was cancelled'));
      signal?.addEventListener('abort', cancel, {once: true});
      if (signal?.aborted) cancel();
    });
  }
}
